<template>
    <div v-if="isPaymentSuccess" class="rlms-flex rlms-flex-col rlms-gap-30 rlms-items-center rlms-p-3">
        <div class="rlms-container-sm">
            <div class="success-container">
                <div class="success-content">
                    <img :src="purchaseSuccessful" alt="Success" class="success-icon rlms-mx-auto" />
                    <h1 class="rlms-fs-xl rlms-fw-600">Payment Successful!</h1>
                    <p class="rlms-fs-md">Thank you for your purchase. Your payment has been processed successfully.</p>
                    <div class="purchase-details">
                        <h3 class="rlms-fs-lg rlms-fw-500">Purchase Details</h3>
                        <ul v-for="(item, key) in checkoutSession.metadata" :key="key">
                            <li>
                                <span>Product:</span>
                                <span>{{ item?.name }}</span>
                            </li>
                            <li>
                                <span>Quantity:</span>
                                <span>{{ item?.quantity }} learner{{ item?.quantity > 1 ? "'s" : '' }}</span>
                            </li>
                            <li>
                                <span>Amount:</span>
                                <span>{{ formatCurrency(item?.price * item?.quantity) }}</span>
                            </li>
                            <li>
                                <span>Date:</span>
                                <span>{{ formatDateTimestamp(checkoutSession?.created) }}</span>
                            </li>
                        </ul>
                    </div>

                    <p class="rlms-fs-sm">Your subscription is now active and ready to assign to learners.</p>

                    <div class="action-buttons">
                        <RouterLink :to="`/organizations/${organizationId}/learners`" class="rlms-btn rlms-btn-primary"
                            id="assign-now-btn">Assign to Learners Now</RouterLink>
                        <RouterLink class="rlms-btn rlms-btn-outline-primary"
                            :to="`/organizations/${organizationId}/subscription`">Go to Subscriptions</RouterLink>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20" v-if="!loading && !isPaymentSuccess">
        <RouterLink :to="`/organizations/${organizationId}/subscription`"
            class="rlms-flex rlms-items-center rlms-gap-10  rlms-border-bottom rlms-pb-4">
            <img :src="cartBlade1" width="24" height="24" alt="">
            <div class="rlms-fs-lg rlms-fw-600">Shopping Cart</div>
        </RouterLink>
        <div class="cart">
            <div class="cart-left" id="cart-empty">

                <div class="cart-product-box-wrapper" v-if="cartStore.cartItems.length > 0">
                    <p class="rlms-fs-md rlms-fw-600">Cart Items</p>
                    <div class="cart-product-box" v-for="item in cartStore.cartItems"
                        :key="item.productId + '-' + item.languageId">
                        <div class="cart-product-box-head">
                            <div class="cart-product-box-image">
                                <img :src="productImage1" alt="Product Image" />
                            </div>
                            <div class="product-box-info">
                                <div class="product-title-box">
                                    <b class="rlms-fs-md">{{ item.name }}</b>
                                    <a href="javascript:void(0);"
                                        @click="cartStore.removeFromCart(item.productId, item.languageId)"><img
                                            :src="cartBlade2" width="25" height="25" class="rlms-cursor-pointer"
                                            alt=""></a>
                                </div>
                                <div class="rlms-flex rlms-flex-col rlms-gap-10">
                                    <p>Annual Subscription for 1 User</p>
                                </div>
                            </div>
                        </div>
                        <div class="cart-product-box-footer">
                            <p class="rlms-fs-xl rlms-fw-700 rlms-text-primary-500">
                                <span class="product-price">{{ formatCurrency(item.price) }}</span>
                            </p>
                            <div class="cart-product-quantity-box">
                                <span class="rlms-fw-600 rlms-text-secondary-lighter rlms-font-xs">Quantity</span>
                                <div class="rlms-quantity-control">
                                    <div class="quantity-decrease"
                                        @click="cartStore.decreaseCartQuantity(item.productId, item.languageId)">
                                        <img :src="minusIcon" alt="Decrease" />
                                    </div>
                                    <input type="number" min="1" :value="item.quantity"
                                        @input="cartStore.setCartQuantity(item.productId, item.languageId, +$event.target.value)"
                                        class="quantity-input" />
                                    <div class="quantity-increase"
                                        @click="cartStore.increaseCartQuantity(item.productId, item.languageId)">
                                        <img :src="plusIcon" alt="Increase" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty Cart Fallback -->
                <div v-else
                    class="rlms-flex rlms-flex-col rlms-justify-center rlms-items-center rlms-gap-10 rlms-py-desk50-mob-30">
                    <img :src="cartEmpty" width="200">
                    <span>Your Cart is Empty!</span>
                    <p class="rlms-text-secondary-lighter">
                        It looks like you haven’t added anything yet. Let’s get started!
                    </p>
                </div>
            </div>
            <div class="cart-right">
                <div class="order-summary">
                    <span class="rlms-fw-500">Order Summary</span>
                    <ul>
                        <li><span>Subtotal</span><b><span class="product-price">{{ formatCurrency(cartStore.cartTotal)
                                    }}</span></b></li>
                        <li><span>Tax</span><b>$<span class="tax-price">0</span></b></li>
                        <li><span>Discount</span><b>$<span class="discount-price">0</span></b></li>
                        <li class="os-total">
                            <div class="rlms-flex rlms-items-center rlms-gap-5">
                                <span class="rlms-fw-500">Total</span>
                                <p class="rlms-fw-500 rlms-text-extra-2">(USD)</p>
                            </div><b><span class="total-price">{{ formatCurrency(cartStore.cartTotal) }}</span></b>
                        </li>
                    </ul>
                </div>
                <button class="rlms-btn rlms-btn-primary proceed-checkout" :disabled="!cartStore.cartCount"
                    @click="redirectToCheckout">Pay with Stripe</button>
                <p class="rlms-text-center or-divider">
                    <span>OR</span>
                </p>
                <button class="rlms-btn rlms-btn-primary proceed-checkout" :disabled="!cartStore.cartCount"
                    @click="generatePurchaseOrder">Purchase Order</button>
            </div>
        </div>
    </div>
</template>
<script setup>
import router from '@/router';
import api from '@/services/axios';
import { useCartStore } from '@/stores/cart';
import { formatCurrency, formatDateTimestamp } from '@/utils/format-helpers';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useRoute } from "vue-router";
import { loadStripe } from '@stripe/stripe-js';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import purchaseSuccessful from '@/assets/images/purchase_successful.png'
import cartBlade1 from '@/assets/images/cart_blade_9f6791df.svg'
import productImage1 from '@/assets/images/product-image1.png'
import cartBlade2 from '@/assets/images/cart_blade_75ca533a.svg'
import minusIcon from '@/assets/images/minus.svg'
import plusIcon from '@/assets/images/plus.svg'
import cartEmpty from '@/assets/images/cart_empty.png'
import { showConfirmDialog, showDialog } from '@/utils/swal';
// import confirmationIcon from '@/assets/images/confirmation.png';

const loader = useLoaderStore();

const stripeKey = process.env.VUE_APP_STRIPE_KEY;

if (!stripeKey) {
    throw new Error('Stripe publishable key is missing');
}

let stripePromise = null;

onMounted(() => stripePromise = loadStripe(stripeKey));
onUnmounted(() => stripePromise = null);

const route = useRoute();
const organizationId = Number(route.params.organization ?? 0);

const sessionId = route.query.session_id;
const checkoutSession = ref(null);
const loading = ref(true);

const isPaymentSuccess = computed(() => {
    return checkoutSession.value?.payment_status === 'paid';
});

const cartStore = useCartStore();

onMounted(async () => {
    if (sessionId) {
        getStripeSession(sessionId);
    } else {
        loading.value = false;
    }
})

const getStripeSession = async (sessionId) => {
    try {
        loader.show();
        const { data } = await api.post(`/organizations/${organizationId}/get-checkout-session`, {
            sessionId
        });

        checkoutSession.value = data.data;
        checkoutSession.value.metadata = checkoutSession.value?.metadata?.items ? JSON.parse(checkoutSession.value.metadata.items) : null;

        if (checkoutSession.value?.payment_status === 'paid') {
            cartStore.clearCartLocalStorage();
        }
    } catch (error) {
        handleError(error);
    } finally {
        loading.value = false;
        loader.hide();
    }
}

const redirectToCheckout = async () => {
    try {
        loader.show();
        if (!cartStore.cartCount) {
            router.push(`/organizations/${organizationId}/subscription`);
        }

        await api.post(`/organizations/${organizationId}/create-checkout-session`, {
            products: cartStore.cartItems,
        }).then(async (response) => {
            const data = response?.data;
            const sessionId = data.data;

            const stripe = await stripePromise;

            await stripe.redirectToCheckout({
                sessionId: sessionId,
            });
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const generatePurchaseOrder = async () => {
    try {

        if (!cartStore.cartItems?.length) {
            return;
        }

        const result = await showConfirmDialog({
            title: 'Purchase information',
            html: '<p id="text-message">Click the button below to generate your order quote. You can cancel if you want to make changes.</p>',
            showCancelButton: true,
            confirmButtonColor: '#654eff',
            cancelButtonColor: '#654eff',
            confirmButtonText: 'Generate Quote',
            imageUrl: purchaseSuccessful,
        });

        if (result.isConfirmed) {
            loader.show();

            try {
                await api.post(`/organizations/${organizationId}/generate-purchase-order`, {
                    products: cartStore.cartItems,
                });

                cartStore.clearCartLocalStorage();

                showDialog({
                    icon: 'success',
                    title: `Purchase Order Created!`,
                    html: `<p> Your Purchase Order quote has been created, and an email has been sent. If you have any questions or need assistance, please don't hesitate to contact us! </p>`
                });

                router.push(`/organizations/${organizationId}/subscription`);
            } catch (error) {
                handleError(error);
            } finally {
                loader.hide();
            }
        }

    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};
</script>